"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.HDSProfile = exports.PROFILE_FIELDS = void 0;
exports.getHookedProfileConnection = getHookedProfileConnection;
const getAttachmentUrl_ts_1 = require("../toolkit/getAttachmentUrl.js");
const localizeText_ts_1 = require("../localizeText.js");
const HDSSettings_ts_1 = require("./HDSSettings.js");
/**
 * Profile field definitions — each maps to a Pryv event in the profile/ stream tree.
 * These are account-level, shared across all connections (unlike HDSSettings which is per-app).
 *
 * Two groups:
 * - **Identity** (displayName…country) — who the account holder is.
 * - **Account preferences** (preferredLocales…unitSystem) — how they want data presented,
 *   shared across every HDS app. They reuse the `settings/*` event types of the per-app
 *   HDSSettings (so no new data-model types are needed) but live on the account-level
 *   `profile-preferences` stream, which is what makes them interoperable. `theme` is
 *   deliberately NOT here — it stays per-app on HDSSettings.
 */
exports.PROFILE_FIELDS = {
    displayName: { streamId: 'profile-display-name', eventType: 'contact/display-name' },
    avatar: { streamId: 'profile-avatar', eventType: 'picture/*' },
    name: { streamId: 'profile-name', eventType: 'contact/name' },
    surname: { streamId: 'profile-name', eventType: 'contact/surname' },
    dateOfBirth: { streamId: 'profile-date-of-birth', eventType: 'date/iso-8601' },
    sex: { streamId: 'profile-sex', eventType: 'attributes/biological-sex' },
    country: { streamId: 'profile-address', eventType: 'contact/country' },
    preferredLocales: { streamId: 'profile-preferences', eventType: 'settings/preferred-locales' },
    timezone: { streamId: 'profile-preferences', eventType: 'settings/timezone' },
    dateFormat: { streamId: 'profile-preferences', eventType: 'settings/date-format' },
    unitSystem: { streamId: 'profile-preferences', eventType: 'settings/unit-system' },
};
const DEFAULTS = {
    displayName: null,
    avatar: null,
    name: null,
    surname: null,
    dateOfBirth: null,
    sex: null,
    country: null,
    preferredLocales: ['en'],
    timezone: 'Europe/Zurich',
    dateFormat: 'DD.MM.YYYY',
    unitSystem: 'metric',
};
/**
 * Browser-inferred defaults for the preferences that can be detected. Applied over
 * DEFAULTS but under stored values. Same inference as HDSSettings.
 */
function browserDefaults() {
    if (typeof navigator === 'undefined')
        return {};
    const result = {};
    try {
        const lang = navigator.language?.split('-')[0];
        if (lang)
            result.preferredLocales = [lang];
    }
    catch { /* ignore */ }
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz)
            result.timezone = tz;
    }
    catch { /* ignore */ }
    return result;
}
/**
 * Push the profile's locale to the localizer — but ONLY when it is genuinely **stored**.
 *
 * L1 fix (plan 78): the profile is the account-level source of truth for locale, but an
 * *unstored* profile has no opinion. Applying its default (`['en']` / browser inference)
 * unconditionally silently clobbered a legacy per-app language: `setPreferredLocales`
 * prepends, and consumers (e.g. doctor-dashboard) hook HDSProfile *after* HDSSettings, so
 * HDSProfile's default landed last and won. A user who had picked Français in the old
 * per-app selector (stored in HDSSettings; profile empty) flipped to English on next load.
 *
 * Gating on `isStored` mirrors `resolveAccountPreference` exactly: profile locale wins only
 * when the user explicitly stored an account-level value; otherwise the per-app / browser
 * value already applied by HDSSettings stands.
 */
function applyLocaleIfStored() {
    if (_cache.preferredLocales === undefined)
        return;
    try {
        (0, localizeText_ts_1.setPreferredLocales)(_values.preferredLocales);
    }
    catch { /* locale may not be supported — ignore */ }
}
/** @internal */
let _connection = null;
/** @internal */
let _cache = {};
/** @internal */
let _values = { ...DEFAULTS };
/** @internal */
let _hooked = false;
/** @internal — tracks which streams have been verified/created */
let _ensuredStreams = new Set();
/** @internal The connection HDSProfile is hooked to, or null (used by `saveLanguage`). */
function getHookedProfileConnection() {
    return _connection;
}
/**
 * Check if event type matches a field's eventType (supports `picture/*` wildcard).
 */
function typeMatches(eventType, fieldType) {
    if (fieldType.endsWith('/*')) {
        return eventType.startsWith(fieldType.slice(0, -1));
    }
    return eventType === fieldType;
}
/**
 * Match an event to a profile field by eventType + streamId.
 */
function matchField(event) {
    for (const [key, field] of Object.entries(exports.PROFILE_FIELDS)) {
        if (typeMatches(event.type, field.eventType) &&
            event.streamIds?.includes(field.streamId)) {
            return key;
        }
    }
    return null;
}
/**
 * Resolve avatar URL from a picture event.
 * Handles: attachment (construct API URL), data URL in content, plain URL in content.
 */
function resolveAvatarUrl(event, conn = _connection) {
    // Attachment — use shared getAttachmentUrl utility
    if (event.attachments?.length > 0 && conn) {
        return (0, getAttachmentUrl_ts_1.getAttachmentUrl)(conn, event);
    }
    // Content is a string (data URL or plain URL)
    if (typeof event.content === 'string' && event.content.length > 0) {
        return event.content;
    }
    return null;
}
/**
 * Read profile from any connection (read-only, does not affect the singleton).
 * Use this to read someone else's profile via a shared connection.
 * Returns null values for fields the connection cannot access.
 */
async function readProfileFromConnection(conn) {
    // No browserDefaults() here: this reads *someone else's* profile, and inferring a
    // contact's locale/timezone from the local browser would be plainly wrong.
    const values = { ...DEFAULTS };
    let events;
    try {
        events = await conn.apiOne('events.get', { streams: ['profile'], limit: 100 }, 'events');
    }
    catch {
        // Connection may not have access to profile/ streams
        return values;
    }
    // First event per field wins. Tracked with a Set rather than a `=== null` test:
    // the preference fields have non-null defaults, so a null-check would never match.
    const seen = new Set();
    for (const event of events) {
        const key = matchField(event);
        if (key && !seen.has(key)) {
            seen.add(key);
            if (key === 'avatar') {
                values.avatar = resolveAvatarUrl(event, conn);
            }
            else {
                values[key] = event.content;
            }
        }
    }
    return values;
}
async function trashExistingAvatar() {
    const existing = _cache.avatar;
    if (existing && _connection) {
        await _connection.apiOne('events.update', { id: existing.id, update: { trashed: true } }, 'event');
        delete _cache.avatar;
        _values.avatar = null;
    }
}
/**
 * Ensure a profile child stream exists, creating the parent `profile` stream and the child if needed.
 */
async function ensureStream(streamId) {
    if (_ensuredStreams.has(streamId) || !_connection)
        return;
    try {
        // Create parent 'profile' stream (idempotent — ignores "already exists" error)
        await _connection.apiOne('streams.create', { id: 'profile', name: 'Profile' }, 'stream').catch(() => { });
        // Create child stream
        await _connection.apiOne('streams.create', { id: streamId, name: streamId, parentId: 'profile' }, 'stream').catch(() => { });
        _ensuredStreams.add(streamId);
    }
    catch {
        // best effort — the subsequent events.create will fail with a clear error if stream is truly missing
    }
}
async function load() {
    if (!_connection)
        return;
    let events;
    try {
        events = await _connection.apiOne('events.get', { streams: ['profile'], limit: 100 }, 'events');
    }
    catch {
        // Connection may not have access to profile/ streams (e.g. app-scoped permission), or a
        // transient fetch error. Either way, do NOT wipe already-loaded values (P1): a failed
        // *reload* must not un-store account preferences. On a first-ever load establish the
        // defaults so getters work; on a reload keep the last good values.
        if (!_hooked) {
            _values = { ...DEFAULTS, ...browserDefaults() };
            _cache = {};
        }
        // Nothing newly stored → do not assert a locale (would clobber a legacy per-app value — L1).
        _hooked = true;
        return;
    }
    // Build into locals and swap in only after a successful fetch (P1) — so a failed reload
    // never transiently drops account values to defaults. Precedence: stored > browser > DEFAULTS.
    const nextValues = { ...DEFAULTS, ...browserDefaults() };
    const nextCache = {};
    for (const event of events) {
        const key = matchField(event);
        if (key && !nextCache[key]) {
            nextCache[key] = event;
            if (key === 'avatar') {
                nextValues.avatar = resolveAvatarUrl(event);
            }
            else {
                nextValues[key] = event.content;
            }
        }
    }
    _values = nextValues;
    _cache = nextCache;
    applyLocaleIfStored();
    _hooked = true;
}
/**
 * HDSProfile — singleton for account-level data stored in profile/ streams.
 *
 * Unlike HDSSettings (per-app), profile data is shared across all connections. It holds
 * both **identity** (displayName, avatar, name, …) and, since plan 78 §C 7.1b, the
 * **account preferences** every HDS app should agree on:
 * `preferredLocales`, `timezone`, `dateFormat`, `unitSystem`.
 *
 * Why here and not HDSSettings: HDSSettings binds to an app's baseStream, so each app gets
 * its own private copy (hds-webapp → `applications/app-client-dr-form`, doctor-dashboard →
 * `applications/app-dr-hds`) and the values never interoperate. Account-level preferences
 * must outlive any single app, so they live on the account's own `profile-preferences`
 * stream. `theme` stays on HDSSettings — it is legitimately per-app.
 *
 * Usage:
 *   await HDSProfile.hookToConnection(connection);
 *   const name = HDSProfile.get('displayName');
 *   await HDSProfile.set('dateFormat', 'YYYY-MM-DD');
 *
 * Fallback chain for displayName:
 *   HDSSettings.get('displayName') ?? HDSProfile.get('displayName') ?? null
 */
const HDSProfile = {
    /**
     * Read profile from any connection (read-only, does not affect the singleton).
     * Use this to read a contact's profile via a shared connection.
     *
     * Example:
     *   const contactProfile = await HDSProfile.readFromConnection(invite.connection);
     *   const name = contactProfile.displayName;
     *   const avatarUrl = contactProfile.avatar; // resolved URL ready for <img src>
     */
    async readFromConnection(connection) {
        return readProfileFromConnection(connection);
    },
    /**
     * Hook to a Pryv Connection — loads profile from profile/ streams.
     */
    async hookToConnection(connection) {
        _connection = connection;
        await load();
    },
    /**
     * Get the current value for a profile field.
     */
    get(key) {
        return _values[key];
    },
    /**
     * Get all current profile values.
     */
    getAll() {
        return { ..._values };
    },
    /**
     * Whether a field is actually stored on the server, as opposed to resolving to a
     * default or browser inference.
     *
     * Needed because the account preferences never read as `null`: a caller choosing
     * between an account-level value and a legacy per-app HDSSettings one must not let
     * an unset profile default mask a value the user really did set. See
     * `resolveAccountPreference`.
     */
    isStored(key) {
        return _cache[key] !== undefined;
    },
    /**
     * Set a profile value — persists to HDS server and updates cache.
     */
    async set(key, value) {
        if (!_connection) {
            throw new Error('HDSProfile: call hookToConnection() first');
        }
        // Clearing the avatar trashes its event: nulling the content would keep the attachment, and the
        // URL resolves from the attachment, so the photo came back on the next load (B-2026-09-25-3).
        if (key === 'avatar' && value === null) {
            await trashExistingAvatar();
            return;
        }
        const field = exports.PROFILE_FIELDS[key];
        const existing = _cache[key];
        // Clearing any other field deletes its event, as HDSSettings.setDynamic(key, null) does: an event with
        // `content: null` would keep the field "stored" (B-2026-10-28-10). The value falls back as on a load.
        if (value === null) {
            if (existing)
                await (0, HDSSettings_ts_1.deleteForGood)(_connection, existing.id);
            delete _cache[key];
            _values[key] = { ...DEFAULTS, ...browserDefaults() }[key];
            return;
        }
        if (existing) {
            const updated = await _connection.apiOne('events.update', { id: existing.id, update: { content: value } }, 'event');
            _cache[key] = updated;
        }
        else {
            await ensureStream(field.streamId);
            const created = await _connection.apiOne('events.create', { streamIds: [field.streamId], type: field.eventType, content: value }, 'event');
            _cache[key] = created;
        }
        _values[key] = value;
        // _cache[key] was just set above, so this now applies (the value is stored).
        if (key === 'preferredLocales')
            applyLocaleIfStored();
    },
    /**
     * Get avatar URL — resolves from attachment, data URL, or plain URL.
     * Returns null if no avatar event exists.
     */
    getAvatarUrl() {
        const event = _cache.avatar;
        if (!event)
            return null;
        return resolveAvatarUrl(event);
    },
    /**
     * Remove the avatar: trashes its event (attachment included), so no avatar resolves on the next
     * load. Same as `set('avatar', null)`. No-op when there is no avatar.
     */
    async removeAvatar() {
        if (!_connection) {
            throw new Error('HDSProfile: call hookToConnection() first');
        }
        await trashExistingAvatar();
    },
    /**
     * Set avatar from a file (Blob/Buffer) — creates a picture/attached event with attachment.
     * If an avatar event already exists, it is trashed and a new one is created.
     *
     * @param fileData - Blob (browser) or Buffer (Node.js)
     * @param filename - e.g. 'avatar.png'
     */
    async setAvatarFromFile(fileData, filename) {
        if (!_connection) {
            throw new Error('HDSProfile: call hookToConnection() first');
        }
        await trashExistingAvatar();
        await ensureStream(exports.PROFILE_FIELDS.avatar.streamId);
        const result = await _connection.createEventWithFileFromBuffer({ type: 'picture/attached', streamIds: [exports.PROFILE_FIELDS.avatar.streamId] }, fileData, filename);
        _cache.avatar = result.event;
        _values.avatar = resolveAvatarUrl(result.event);
    },
    /**
     * Set avatar from a base64 data URL string — creates a picture/base64 event.
     * If an avatar event already exists, it is trashed and a new one is created.
     *
     * @param dataUrl - e.g. 'data:image/png;base64,iVBOR...'
     */
    async setAvatarFromDataUrl(dataUrl) {
        if (!_connection) {
            throw new Error('HDSProfile: call hookToConnection() first');
        }
        await trashExistingAvatar();
        await ensureStream(exports.PROFILE_FIELDS.avatar.streamId);
        const created = await _connection.apiOne('events.create', { streamIds: [exports.PROFILE_FIELDS.avatar.streamId], type: 'picture/base64', content: dataUrl }, 'event');
        _cache.avatar = created;
        _values.avatar = dataUrl;
    },
    /**
     * Whether profile has been loaded from the server.
     */
    get isHooked() {
        return _hooked;
    },
    /**
     * Reload profile from the server.
     */
    async reload() {
        await load();
    },
    /**
     * @internal Test-only: mark as hooked and inject stored values without a server.
     * Values passed here read as *stored* (`isStored` true), matching a loaded event;
     * omitted fields stay at their default and read as not stored.
     */
    _testHook(stored = {}) {
        _values = { ...DEFAULTS, ...stored };
        _cache = {};
        for (const key of Object.keys(stored)) {
            _cache[key] = { id: `test-${key}`, content: stored[key] };
        }
        _hooked = true;
    },
    /**
     * Reset to defaults (in-memory only — does not delete server events).
     */
    unhook() {
        _connection = null;
        _cache = {};
        _values = { ...DEFAULTS };
        _hooked = false;
        _ensuredStreams = new Set();
    },
};
exports.HDSProfile = HDSProfile;
exports.default = HDSProfile;
